import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { Button } from 'src/components/ui/button';
import { useAppSelector } from 'src/store';
import { useParams } from 'react-router-dom';
import { baseApi, useAddIntegrationMutation, useGetProjectByIdQuery } from 'src/services';
import { useNotification } from 'src/hooks/useNotification';
import { useDispatch } from 'react-redux';
import { useLoader } from 'src/provider/LoaderProvider';
import { setSelectedIntegration, setOpenFinalPopup } from 'src/store/slices/integrationSlice';
import { IntegrationModalStep1 } from 'src/pages/ControlDeskPage/IntegrationStatusSection/IntegrationsModal/IntegrationModalStep1/IntegrationModalStep1';
import { IntegrationModalStep3 } from 'src/pages/ControlDeskPage/IntegrationStatusSection/IntegrationsModal/IntegrationModalStep3/IntegrationModalStep3';
import { IntegrationModalStep2 } from 'src/pages/ControlDeskPage/IntegrationStatusSection/IntegrationsModal/IntegrationModalStep2/IntegrationModalStep2';
import { IntegrationModalStep4 } from 'src/pages/ControlDeskPage/IntegrationStatusSection/IntegrationsModal/IntegrationModalStep4/IntegrationModalStep4';
import { IntegrationModalStep5 } from 'src/pages/ControlDeskPage/IntegrationStatusSection/IntegrationsModal/IntegrationModalStep5/IntegrationModalStep5';

const ArrowIcon = () => {
  return (
    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0 5.0625L6.84562 5.0625L3.70125 8.20688L4.5 9L9 4.5L4.5 -9.81574e-10L3.70687 0.793125L6.84562 3.9375L0 3.9375V5.0625Z"
        fill="#05050D"
      />
    </svg>
  );
};

const INITIAL_STEP = 1;

export const IntegrationsModal = ({
  initialStep = INITIAL_STEP,
  trigger = null,
  visible = true,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const { id } = useParams();
  const [step, setStep] = useState(initialStep);
  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const notification = useNotification();
  const dispatch = useDispatch();
  const { isInactivePlan } = useAppSelector((s) => s.planSlice);
  const [
    addIntegration,
    {
      data: addIntegrationRes,
      isError: isErrorAddIntegration,
      isLoading: isLoadingAddIntegration,
      isSuccess: isSuccessAddIntegration,
    },
  ] = useAddIntegrationMutation() as any;
  const loader = useLoader();
  const createIntegration = (data, skip = false) => {
    if (skip) {
      return setStep(step + 1);
    }

    return addIntegration({ id, body: data });
  };

  const close = (isFinal = false) => {
    dispatch(baseApi.util.invalidateTags(['ProjectById']));
    setOpen(false);

    if (isFinal) {
      dispatch(setOpenFinalPopup(true));
    }
  };

  const onOpenChange = (open) => {
    if (!open) {
      setStep(initialStep);
      dispatch(baseApi.util.invalidateTags(['ProjectById']));
    }

    setOpen(open);
  };

  useEffect(() => {
    if (isSuccessAddIntegration && addIntegrationRes) {
      notification.success(
        `Integration ${selectedIntegration?.friendlyName || addIntegrationRes.integration_type} was added.`,
      );
      loader.hide();

      if (selectedIntegration && selectedIntegration.setupSteps > step) {
        dispatch(setSelectedIntegration({ ...selectedIntegration, ...addIntegrationRes }));
        setStep(step + 1);
      }

      if (selectedIntegration && selectedIntegration.setupSteps === step) {
        close();
      }
    }
  }, [isSuccessAddIntegration, addIntegrationRes]);

  useEffect(() => {
    if (isLoadingAddIntegration) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingAddIntegration]);

  useEffect(() => {
    if (isErrorAddIntegration) {
      loader.hide();
    }
  }, [isErrorAddIntegration]);

  return (
    <Dialog closeButtonHidden onOpenChange={onOpenChange} open={open}>
      {visible &&
        (trigger || (
          <DialogTrigger disabled={disabled || !visible} asChild>
            <Button
              disabled={disabled || !currentProject?.is_active || isInactivePlan}
              variant="outline"
              className={clsx(!visible && 'pointer-events-none opacity-50', 'flex gap-2')}
            >
              <p className="font-medium text-base-xs">Launch</p>
            </Button>
          </DialogTrigger>
        ))}
      <DialogContent closeButtonHidden className="pb-0 !max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            <div className="flex w-full justify-between">
              <div className="flex flex-col gap-[3px]">
                <h4 className={'opacity-80 text-lg font-medium font-jamjuree'}>
                  {selectedIntegration
                    ? `${selectedIntegration.friendlyName} Integration`
                    : 'Launch AI Agent'}
                </h4>
                {selectedIntegration && selectedIntegration.setupGuideUrl && (
                  <a
                    href={selectedIntegration.setupGuideUrl}
                    target={'_blank'}
                    rel="noopener noreferrer"
                    className={'text-[#7A6BE5] text-xs font-medium font-jamjuree hover:underline'}
                  >
                    Setup Guide →
                  </a>
                )}
              </div>
              <p className="font-jamjuree font-medium text-xs flex items-center gap-1.5 opacity-40">
                Step {!selectedIntegration ? '1' : step}{' '}
                {selectedIntegration ? `of ${selectedIntegration.setupSteps}` : ''}
                <ArrowIcon />
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          {step === 1 && (
            <IntegrationModalStep1
              project={currentProject}
              onClick={(data) => {
                dispatch(setSelectedIntegration(data));
                setStep(step + 1);
              }}
              createIntegration={createIntegration}
              close={close}
            />
          )}
          {step === 2 && (
            <IntegrationModalStep2
              onSubmit={createIntegration}
              onBackButton={() => {
                dispatch(setSelectedIntegration(null));
                setStep(step - 1);
              }}
              close={close}
            />
          )}
          {step === 3 && (
            <IntegrationModalStep3
              defaultBtnStyle
              onClick={(data) => {
                data && dispatch(setSelectedIntegration(data));
                setStep(step + 1);
              }}
              close={close}
            />
          )}
          {step === 4 && (
            <IntegrationModalStep4
              defaultBtnStyle
              onClick={(data) => {
                dispatch(setSelectedIntegration(data));
                setStep(step + 1);
              }}
              close={close}
            />
          )}
          {step === 5 && (
            <IntegrationModalStep5
              defaultBtnStyle
              onClick={(data) => {
                dispatch(setSelectedIntegration(data));
                setStep(step + 1);
              }}
              close={close}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
